import { Tick } from "@/design-system/Check/Check";
import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import { StreamlineSource } from "@repo/shared/types";

/*
  Where one card comes from. The market price is on the card itself.

  Given `onToggle` the chip is also the card's Done control: a check sits
  before the label and the whole chip is the hit target. Checked, it turns to
  the success colour and the card behind it is the user's to unmark.
*/
export const SourceChip = ({
  source,
  small = false,
  checked = false,
  onToggle,
  className,
}: {
  source: StreamlineSource;
  /** Under a phone-sized card, so the chip is not wider than the card. */
  small?: boolean;
  checked?: boolean;
  /** Makes the chip a Done checkbox for its card. */
  onToggle?: () => void;
  className?: string;
}) => {
  const { t } = useTranslation("sbcs");
  const club = source !== "MARKET";
  const label =
    source === "MARKET"
      ? t("source.buy")
      : source === "SBC-STORAGE"
        ? t("source.sbcStorage")
        : source === "TRANSFER-LIST"
          ? t("source.transferList")
          : source === "DUPLICATES"
            ? t("source.duplicate")
            : t("source.inYourClub");
  const classes = cn(
    /* Same corner radius as the row's Done checkbox, so the two read as kin. */
    /* Never wider than its box: a long label ("Armazenamento de DCP") truncates. */
    "inline-flex max-w-full min-w-0 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg border",
    small ? "px-2 py-0.5" : "px-3 py-1",
    checked
      ? "border-streamline-success bg-streamline-success text-streamline-on-success"
      : club
        ? "border-sbc-club-border bg-sbc-club-surface text-sbc-club"
        : /* A whisper of the coin colour, so Buy reads as the club chip's
             sibling rather than a plain grey pill. */
          "border-sbc-coin/25 bg-sbc-chip bg-linear-to-b from-sbc-coin/10 to-sbc-chip text-sbc-coin",
    className,
  );
  const content = (
    <>
      {onToggle && (
        <span
          className={cn(
            "flex size-3 shrink-0 items-center justify-center rounded-[3px] border",
            checked
              ? "border-streamline-on-success bg-streamline-on-success/15"
              : "border-current opacity-60",
          )}
        >
          {checked && <Tick className="size-2" />}
        </span>
      )}
      <span
        className={cn(
          "min-w-0 truncate font-bold",
          small ? "text-[10px]" : "text-xs",
        )}
      >
        {label}
      </span>
    </>
  );
  if (!onToggle)
    return (
      <span title={label} className={classes}>
        {content}
      </span>
    );
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      title={label}
      aria-label={
        checked ? t("streamline.unmarkCard") : t("streamline.markCard")
      }
      onClick={onToggle}
      className={cn(
        classes,
        "cursor-pointer transition-[filter,background-color,border-color] duration-200 hover:brightness-110 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-neutral-400",
      )}
    >
      {content}
    </button>
  );
};
